import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../api/apiConfig';
import { useAuth } from './AuthContext';

type CallState = 'idle' | 'connecting' | 'ringing' | 'active' | 'ended' | 'failed';

interface PhoneContextType {
  callState: CallState;
  isMuted: boolean;
  callDuration: number;
  currentNumber: string | null;
  isReady: boolean;
  statusMessage: string | null;
  makeCall: (number: string, personId?: number) => void;
  hangup: () => void;
  toggleMute: () => void;
}

const PhoneContext = createContext<PhoneContextType>({
  callState: 'idle',
  isMuted: false,
  callDuration: 0,
  currentNumber: null,
  isReady: false,
  statusMessage: null,
  makeCall: () => {},
  hangup: () => {},
  toggleMute: () => {},
});

export const usePhone = () => useContext(PhoneContext);

export const PhoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [callState, setCallState] = useState<CallState>('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [currentNumber, setCurrentNumber] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const plivoRef = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const personIdRef = useRef<number | undefined>(undefined);

  const startTimer = useCallback(() => {
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const initPlivo = async () => {
      try {
        const { default: Plivo } = await import('plivo-browser-sdk');
        const plivoBrowserSdk = new Plivo({
          debug: 'OFF' as any,
          permOnClick: true,
          enableTracking: false,
          closeProtection: false,
          maxAverageBitrate: 48000,
        } as any);

        plivoBrowserSdk.client.on('onWebrtcNotSupported', () => {
          setStatusMessage('WebRTC not supported on this browser');
        });

        plivoBrowserSdk.client.on('onLogin', () => {
          setIsReady(true);
          setStatusMessage(null);
        });

        plivoBrowserSdk.client.on('onLoginFailed', (error: any) => {
          setIsReady(false);
          setStatusMessage('Plivo login failed: ' + (error || 'unknown'));
        });

        plivoBrowserSdk.client.on('onCallRemoteRinging', () => {
          setCallState('ringing');
          setStatusMessage('Ringing...');
        });

        plivoBrowserSdk.client.on('onCallAnswered', () => {
          setCallState('active');
          setStatusMessage(null);
          startTimer();
        });

        plivoBrowserSdk.client.on('onCallTerminated', (evt: any) => {
          setCallState('ended');
          setStatusMessage('Call ended');
          stopTimer();
          setTimeout(() => {
            setCallState('idle');
            setCurrentNumber(null);
            setCallDuration(0);
            setIsMuted(false);
            setStatusMessage(null);
          }, 3000);
        });

        plivoBrowserSdk.client.on('onCallFailed', (reason: any) => {
          setCallState('failed');
          setStatusMessage('Call failed: ' + (reason?.message || reason || 'unknown'));
          stopTimer();
          setTimeout(() => {
            setCallState('idle');
            setCurrentNumber(null);
            setStatusMessage(null);
          }, 5000);
        });

        plivoBrowserSdk.client.on('onMediaPermission', (permissionResult: any) => {
          if (!permissionResult?.status) {
            setStatusMessage('Microphone permission denied');
          }
        });

        // Get credentials and login
        setStatusMessage('Connecting to Plivo...');
        const response = await apiClient.get('/api/plivo/token/');
        const { username, password } = response.data;

        // Use username/password login (more reliable than access tokens)
        plivoBrowserSdk.client.login(username, password);

        plivoRef.current = plivoBrowserSdk;
      } catch (err: any) {
        setStatusMessage('Plivo init error: ' + (err?.message || err));
      }
    };

    initPlivo();

    return () => {
      stopTimer();
      if (plivoRef.current) {
        try {
          plivoRef.current.client.logout();
        } catch {}
      }
    };
  }, [isAuthenticated, startTimer, stopTimer]);

  const makeCall = useCallback((number: string, personId?: number) => {
    if (!plivoRef.current || callState !== 'idle') return;

    setCurrentNumber(number);
    setCallState('connecting');
    personIdRef.current = personId;

    const extraHeaders = {
      'X-PH-callerid': number,
    };

    setStatusMessage('Placing call...');
    const result = plivoRef.current.client.call(number, extraHeaders);
    if (!result) {
      setStatusMessage('Call failed to initiate - SDK not ready');
      setCallState('failed');
      setTimeout(() => {
        setCallState('idle');
        setCurrentNumber(null);
        setStatusMessage(null);
      }, 3000);
    }

    // Log the call
    apiClient.post('/api/plivo/log-call/', {
      destination_number: number,
      person_id: personId,
    }).catch(err => console.error('Failed to log call:', err));
  }, [callState]);

  const hangup = useCallback(() => {
    if (plivoRef.current) {
      plivoRef.current.client.hangup();
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!plivoRef.current) return;
    if (isMuted) {
      plivoRef.current.client.unmute();
    } else {
      plivoRef.current.client.mute();
    }
    setIsMuted(!isMuted);
  }, [isMuted]);

  return (
    <PhoneContext.Provider value={{
      callState,
      isMuted,
      callDuration,
      currentNumber,
      isReady,
      statusMessage,
      makeCall,
      hangup,
      toggleMute,
    }}>
      {children}
    </PhoneContext.Provider>
  );
};
